import { useCallback, type FC } from "react";
import styled from "styled-components";

import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import {
  selectCampaignItemsIsLoading,
  selectSelectedCampaignItemIndex,
  selectTableCampaignItems,
} from "@ds/modules/content/campaign-items/redux/selectors";
import { campaignItemsActions } from "@ds/modules/content/campaign-items/redux/slice";

import { ActionsBar } from "@ds/components/actions-bar";
import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { IconType } from "@ds/components/icons";

const CAMPAIGN_ITEMS_MAX = 100;

const CapacityNote = styled.span`
  font-size: 1.1rem;
  color: var(--textColorSecondary);
`;

export const CampaignDetailsItemsCardActionsMenu: FC<{
  campaign: Campaign;
  selectedEntities: CampaignItem[];
  onCreate: () => void;
  onUpdate: () => void;
  onBulkWeight: () => void;
  onReorder: (currIndex: number, newIndex: number) => void;
}> = ({ campaign, selectedEntities, onCreate, onUpdate, onBulkWeight, onReorder }) => {
  const isAtCapacity = (campaign.campaign_items_count ?? 0) >= CAMPAIGN_ITEMS_MAX;
  const isLoading = useRootSelector(selectCampaignItemsIsLoading);
  const tableItems = useRootSelector(selectTableCampaignItems);
  const selectedIndex = useDeepRootSelector(selectSelectedCampaignItemIndex);

  const dispatch = useRootDispatch();

  const onDeleteHandler = useCallback(() => {
    dispatch(
      campaignItemsActions.deleteCampaignItems(
        selectedEntities.map(({ id }) => id),
        { campaignId: campaign.id },
      ),
    );
  }, [dispatch, campaign.id, selectedEntities]);

  const onMoveUpHandler = useCallback(() => onReorder(selectedIndex, selectedIndex - 1), [onReorder, selectedIndex]);

  const onMoveDownHandler = useCallback(() => onReorder(selectedIndex, selectedIndex + 1), [onReorder, selectedIndex]);

  const isReorderDisabled = isLoading || selectedEntities.length !== 1;

  return (
    <ActionsBar>
      <Button
        ariaLabel="add playlist button"
        buttonType={ButtonType.Round}
        icon={IconType.Create}
        isDisabled={isAtCapacity}
        onClick={onCreate}
      />
      {isAtCapacity && (
        <CapacityNote>
          {CAMPAIGN_ITEMS_MAX}/{CAMPAIGN_ITEMS_MAX} playlist limit reached
        </CapacityNote>
      )}
      {selectedEntities.length === 1 && (
        <Button ariaLabel="edit item button" buttonType={ButtonType.Round} icon={IconType.Update} onClick={onUpdate} />
      )}
      {selectedEntities.length > 1 && (
        <Button
          ariaLabel="set weight button"
          buttonType={ButtonType.Round}
          icon={IconType.Update}
          onClick={onBulkWeight}
        />
      )}
      {selectedEntities.length > 0 && (
        <Button
          ariaLabel="remove items button"
          buttonType={ButtonType.Round}
          actionType={ButtonActionType.Error}
          icon={IconType.Delete}
          onClick={onDeleteHandler}
        />
      )}
      <Button
        ariaLabel="Move playlist up"
        buttonType={ButtonType.Round}
        icon={IconType.SortUp}
        isDisabled={isReorderDisabled || selectedIndex <= 0}
        onClick={onMoveUpHandler}
      />
      <Button
        ariaLabel="Move playlist down"
        buttonType={ButtonType.Round}
        icon={IconType.SortDown}
        isDisabled={isReorderDisabled || selectedIndex >= tableItems.length - 1}
        onClick={onMoveDownHandler}
      />
    </ActionsBar>
  );
};
